import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, CheckCircle, ArrowLeft, ArrowRight, SkipForward } from "lucide-react";

interface DuplicatePair {
  record_a: any;
  record_b: any;
}

export function DuplicateReview({ onComplete }: { onComplete: () => void }) {
  const [pairs, setPairs] = useState<DuplicatePair[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [resolved, setResolved] = useState(0);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    setLoading(true);

    // Get all records, then find duplicates client-side
    // We need to batch due to 1000 row limit
    let allRecords: any[] = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from("mailing_records")
        .select("*")
        .order("last_name")
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (error || !data || data.length === 0) {
        hasMore = false;
      } else {
        allRecords = [...allRecords, ...data];
        hasMore = data.length === pageSize;
        page++;
      }
    }

    // Group by name + normalized address
    const groups = new Map<string, any[]>();
    for (const r of allRecords) {
      if (!r.first_name || !r.last_name || !r.street_address) continue;
      const key = `${r.first_name.toLowerCase().trim()}|${r.last_name.toLowerCase().trim()}|${r.street_address.toLowerCase().trim()}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    }

    // Build pairs from groups with 2+ records
    const duplicatePairs: DuplicatePair[] = [];
    for (const [, records] of groups) {
      if (records.length < 2) continue;
      // Sort: prefer the one with more mailing history first
      records.sort((a: any, b: any) => (b.times_mailed || 0) - (a.times_mailed || 0));
      for (let i = 1; i < records.length; i++) {
        duplicatePairs.push({ record_a: records[0], record_b: records[i] });
      }
    }

    setPairs(duplicatePairs);
    setLoading(false);
  };

  const handleRemove = async (idToRemove: string) => {
    setRemoving(true);
    const { error } = await supabase.from("mailing_records").delete().eq("id", idToRemove);
    if (error) {
      toast.error("Failed to remove record");
    } else {
      toast.success("Duplicate removed");
      setResolved(r => r + 1);
      if (currentIndex < pairs.length - 1) {
        setCurrentIndex(i => i + 1);
      } else {
        toast.success(`All duplicates reviewed! ${resolved + 1} removed.`);
        onComplete();
      }
    }
    setRemoving(false);
  };

  const handleSkip = () => {
    if (currentIndex < pairs.length - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      toast.info("All duplicates reviewed");
      onComplete();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Scanning for duplicates...</div>;
  }

  if (pairs.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-primary" />
          <p className="text-lg font-medium">No duplicates found!</p>
          <p className="text-sm text-muted-foreground">All records are unique by name + address.</p>
        </CardContent>
      </Card>
    );
  }

  const pair = pairs[currentIndex];
  if (!pair) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-primary" />
          <p className="text-lg font-medium">All done! {resolved} duplicates removed.</p>
          <Button onClick={onComplete} className="mt-4">Back to Records</Button>
        </CardContent>
      </Card>
    );
  }

  const renderRecord = (record: any, label: string, isRecommendedKeep: boolean) => (
    <Card className={`flex-1 ${isRecommendedKeep ? "ring-2 ring-primary" : ""}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{label}</CardTitle>
          {isRecommendedKeep && <Badge>Recommended Keep</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Seller ID" value={record.seller_id} />
        <Row label="Name" value={`${record.first_name} ${record.last_name} ${record.suffix || ""}`} />
        <Row label="Business" value={record.business_name} />
        <Row label="Address" value={`${record.street_address}, ${record.city}, ${record.state} ${record.zip_code}`} />
        <Row label="County" value={record.county} />
        <Row label="Origin State" value={record.origin_state} />
        <Row label="Property" value={record.property_address} />
        <Row label="Doc Type" value={record.document_type} />
        <Row label="Consideration" value={record.consideration ? `$${Number(record.consideration).toLocaleString()}` : null} />
        <Row label="Times Mailed" value={String(record.times_mailed || 0)} highlight={record.times_mailed > 0} />
        <Row label="Last Mailed" value={record.last_mailed_date} />
        <Row label="Response" value={record.response} />
        <Row label="Phone" value={record.phone_number} />
        <Row label="Email" value={record.email_address} />
        <Row label="TM Notes" value={record.tm_notes} />
        <Row label="TM Status" value={record.tm_dropdown} />

        <div className="pt-3">
          <Button
            variant="destructive"
            size="sm"
            className="w-full"
            onClick={() => handleRemove(record.id)}
            disabled={removing}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove This One
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Duplicate Review</h3>
          <p className="text-sm text-muted-foreground">
            Pair {currentIndex + 1} of {pairs.length} · {resolved} resolved
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={currentIndex === 0} onClick={() => setCurrentIndex(i => i - 1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button variant="outline" size="sm" onClick={handleSkip}>
            <SkipForward className="h-4 w-4 mr-1" /> Skip
          </Button>
          <Button variant="outline" size="sm" disabled={currentIndex >= pairs.length - 1} onClick={() => setCurrentIndex(i => i + 1)}>
            Next <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderRecord(pair.record_a, "Record A", (pair.record_a.times_mailed || 0) >= (pair.record_b.times_mailed || 0))}
        {renderRecord(pair.record_b, "Record B", (pair.record_b.times_mailed || 0) > (pair.record_a.times_mailed || 0))}
      </div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | null | undefined; highlight?: boolean }) {
  if (!value || value.trim() === "") return null;
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className={`text-right ${highlight ? "font-bold text-primary" : ""}`}>{value}</span>
    </div>
  );
}
